import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _attempt from "lodash/attempt";
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import Guacamole from 'guacamole-common-js';
import config from '../config';
import { getSupportedMimetypes } from './imageTypeSupport';
export let GuacamoleError = /*#__PURE__*/function (GuacamoleError) {
  GuacamoleError[GuacamoleError["Duplicate"] = 521] = "Duplicate";
  return GuacamoleError;
}({});
export class ConnectedVirtualMachine {
  constructor(parentElement, onError, onRetry, onReady, onMouseDown, environment) {
    var _this = this;
    this.parentElement = parentElement;
    this.onError = onError;
    this.onRetry = onRetry;
    this.onReady = onReady;
    this.onMouseDown = onMouseDown;
    _defineProperty(this, "guacamole", void 0);
    _defineProperty(this, "keyboard", void 0);
    _defineProperty(this, "keyboardLock", false);
    _defineProperty(this, "width", NaN);
    _defineProperty(this, "height", NaN);
    _defineProperty(this, "scaleFactor", 1);
    _defineProperty(this, "dispose", () => {
      _attempt(() => {
        this.guacamole.onsync = null;
        this.guacamole.onstatechange = null;
        this.keyboard.onkeydown = null;
        this.keyboard.onkeyup = null;
        this.parentElement.removeEventListener('mouseenter', this.activateKeyboard);
        this.parentElement.removeEventListener('mouseleave', this.deactivateKeyboard);
        this.parentElement.removeChild(this.guacamole.getDisplay().getElement());
        this.guacamole.disconnect();
      });
    });
    _defineProperty(this, "scale", function () {
      let scaleFactor = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
      _this.scaleFactor = scaleFactor;
      _this.guacamole.getDisplay().scale(scaleFactor);
      _this.updateSize(_this.width, _this.height);
    });
    _defineProperty(this, "updateSize", (width, height) => {
      this.setSize(width, height);
      this.guacamole.sendSize(...this.getOptimalSize(width, height));
    });
    _defineProperty(this, "getOptimalSize", (width, height) => [Math.floor(width / this.scaleFactor), Math.floor(height / this.scaleFactor)]);
    _defineProperty(this, "activateKeyboard", () => {
      this.keyboardLock = true;
    });
    _defineProperty(this, "deactivateKeyboard", () => {
      this.keyboardLock = false;
    });
    const tunnel = new Guacamole.WebSocketTunnel("".concat(config(environment).urls.GUACAMOLE_WEBSOCKET_TUNNEL));
    const onErrorCb = guacamoleStatus => {
      // if "Disconnected by other connection." when opening another tab for instance
      const forceShowReconnector = guacamoleStatus.code === GuacamoleError.Duplicate;
      onError(forceShowReconnector, new Error('Guacamole Error'), guacamoleStatus);
    };
    tunnel.onerror = onErrorCb;
    this.guacamole = new Guacamole.Client(tunnel);
    this.guacamole.onerror = onErrorCb;
    this.connectKeyboard();
    this.connectMouse();
    this.attachElement();
    this.checkSizeMessage();
    this.listenToStateChange();
  }
  async connect(sessionId, width, height) {
    let scaleFactor = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;
    const query = new URLSearchParams();
    this.scaleFactor = scaleFactor;
    this.guacamole.getDisplay().scale(scaleFactor);
    this.setSize(width, height);
    const [optimalWidth, optimalHeight] = this.getOptimalSize(width, height);

    // dpi not set as it causes scaling issues
    // for certain window sizes on retina screens
    query.set('sessionId', sessionId.toString());
    query.set('height', optimalHeight.toString());
    query.set('width', optimalWidth.toString());
    const supportedMimeTypes = await getSupportedMimetypes();
    supportedMimeTypes.forEach(mimeType => query.append('imageType', mimeType));
    this.guacamole.connect(query.toString());
  }
  setSize(width, height) {
    this.width = width;
    this.height = height;
  }
  listenToStateChange() {
    // eslint-disable-next-line consistent-return
    this.guacamole.onstatechange = state => {
      if (state === ConnectedVirtualMachine.DISCONNECTED_STATE) {
        return this.onError();
      }
    };
  }
  attachElement() {
    const guacElement = this.guacamole.getDisplay().getElement();
    guacElement.style.cursor = 'none';
    guacElement.style.zIndex = '0';
    guacElement.setAttribute('data-cy', 'guacamole-client-pane');
    this.parentElement.appendChild(guacElement);
  }
  checkSizeMessage() {
    // When resizing the frame, for unknown reasons, sometimes the first
    // "size" message from the guacamole server does not contain the full screen.
    // As a result, the guacamole client does not render correctly.
    let nbOfSync = 0;
    this.guacamole.onsync = () => {
      nbOfSync += 1;
      if (this.guacamole.getDisplay().getWidth() === 0) {
        if (nbOfSync >= 5) {
          this.onRetry();
          this.guacamole.onsync = null;
        }
      } else {
        this.onReady();
        this.guacamole.onsync = null;
      }
    };
  }
  isKeyboardActive() {
    return this.keyboardLock;
  }
  connectKeyboard() {
    this.keyboard = new Guacamole.Keyboard(document);
    this.keyboard.onkeydown = keysym => {
      if (this.isKeyboardActive()) {
        _attempt(() => this.guacamole.sendKeyEvent(1, keysym));
        return false;
      }
      return true;
    };
    this.keyboard.onkeyup = keysym => {
      if (this.isKeyboardActive()) {
        _attempt(() => this.guacamole.sendKeyEvent(0, keysym));
        return false;
      }
      return true;
    };
    this.parentElement.addEventListener('mouseenter', this.activateKeyboard);
    this.parentElement.addEventListener('mouseleave', this.deactivateKeyboard);
    const activateKeyboardOnce = () => {
      this.activateKeyboard();
      this.parentElement.removeEventListener('mousemove', activateKeyboardOnce, true);
    };
    this.parentElement.addEventListener('mousemove', activateKeyboardOnce, true);
  }
  connectMouse() {
    const display = this.guacamole.getDisplay();
    const mouse = new Guacamole.Mouse(display.getElement());
    mouse.onmousedown = mouseState => {
      _attempt(() => this.guacamole.sendMouseState(mouseState));
      this.onMouseDown();
    };
    mouse.onmouseup = mouseState => {
      _attempt(() => this.guacamole.sendMouseState(mouseState));
    };
    mouse.onmousemove = mouseState => {
      mouseState.x /= display.getScale();
      mouseState.y /= display.getScale();
      _attempt(() => this.guacamole.sendMouseState(mouseState));
    };
  }
}
_defineProperty(ConnectedVirtualMachine, "DISCONNECTED_STATE", 5);
_defineProperty(ConnectedVirtualMachine, "DEFAULT_DPI", 96);